import * as moment from 'moment';
import * as d3 from 'd3';

import { TimeScale } from '../scales/TimeScale';
import { getAxisFormat } from '../../../helpers/timeAxisHelper';

export class TimeAxis {
    private _width: number;
    private _timeFormat: string;
    private _axis: d3.svg.Axis;
    private _group: d3.Selection<any>;
    private _scale: d3.time.Scale<number, number>;
    private _wtScale: TimeScale;
    private _ticksCount: number;
    private _isAxisVisible = true;
    private _isYAxisVisible: boolean = true;

    private _min: Date;
    private _max: Date;

    constructor(
        width: number,
        beginDate: any,
        endDate: any,
        ticksCount: number,
        container: d3.Selection<any>,
        isYAxisVisible: boolean
    ) {
        this._wtScale = new TimeScale(width, beginDate, endDate);
        this._ticksCount = ticksCount;
        const duration = moment.duration(endDate - beginDate);
        this.width(width);
        this._axis = d3.svg.axis()
            .scale(this._scale);
        this._group = container.append('g').classed('time-axis', true);
        this.setTimeFormat(duration);
        this.isYAxisVisible(isYAxisVisible);
    }

    width(value?: number): number {
        if (value) {
            this._width = value;
            this._wtScale.size(this._width);
            this._scale = this._wtScale.scale();
        }
        return this._width;
    }

    scale() {
        return this._scale;
    }

    update(min: any, max: any): void {
        const g = this._group;

        this._wtScale.update(min, max);
        this.updateScale(g);
    }

    translate(translateX: number, translateY: number) {
        const x = isNaN(translateX) ? 0 : translateX;
        const y = isNaN(translateY) ? 0 : translateY;

        this._group
            .attr({
                transform: `translate(${x},${y})`
            });
    }

    resize(width: number): void {
        this.width(width);
        const g = this._group;
        this._wtScale.size(width);
        this.updateScale(g);

    }
    private _ratio: number = 1;
    ratio(): number;
    ratio(value: number): this;
    ratio(value?: number) {
        if (arguments.length) {
            this._ratio = value;
            return this;
        }
        return this._ratio;
    }

    apply() {
        this.updateScale(this._group);
    }

    ticksCount(value?: number): number {
        if (value) {
            this._ticksCount = value;
            if (this._axis) {
                this._axis.ticks(this._ticksCount);
            }
        }
        return this._ticksCount;
    }

    timeFormat(value?: string): string {
        if (value) {
            this._timeFormat = value;
            if (this._axis) {
                this._axis
                    .tickFormat(d3.time.format(this._timeFormat));
            }
        }
        return this._timeFormat;
    }

    min(value?: Date) {
        if (value) {
            this._min = value;
            this.update(this._min, this.scale().domain()[1]);
        }
        return this._min;
    }

    max(value?: Date) {
        if (value) {
            this._max = value;
            this.update(this.scale().domain()[0], this._max);
        }
        return this._max;
    }

    isAxisVisible(value?: boolean): boolean {
        if (value !== null && value !== undefined) {
            this._isAxisVisible = value;
            if (this._isAxisVisible) {
                this._group.style({ 'visibility': 'visible' });
            } else {
                this._group.style({ 'visibility': 'hidden' });
            }
        }
        return this._isAxisVisible;
    }

    isYAxisVisible(value?: boolean): boolean {
        if (value !== null && value !== undefined) {
            this._isYAxisVisible = value;
        }
        return this._isYAxisVisible;
    }

    private updateScale(g: d3.Selection<any>) {
        this._scale = this._wtScale.scale();
        const extent = <any>this._scale.domain()[1] - <any>this._scale.domain()[0];
        const duration = moment.duration(extent);
        this.setTimeFormat(duration);
        this._axis.scale(this._scale);
        this._axis(g);

        g.selectAll('path')
            .classed('axis', true);
        g.selectAll('line')
            .classed('tickLine', true);
        g.selectAll('text')
            .classed('tickLabel', true);
    }

    private setTimeFormat(duration: moment.Duration) {
        if (this._scale) {
            const width = this.width();
            const domain = this._scale.domain();
            // Divide the width by the ratio, to fake a wider axis, accounting for the font size decrease
            const { format, ticks } = getAxisFormat(duration, width / (this._ratio || 1), domain, this._isYAxisVisible);
            this.timeFormat(format.format);
            this._axis.tickValues(ticks);
            // #38914: Remove first X-Axis label if Y-Axis is hidden
            if (ticks && ticks.length > 0 && this._isYAxisVisible === false) {
                const tickToHide = d3.select('.time-axis g.tick:first-child');
                if (!tickToHide.empty()) {
                    tickToHide.style({ 'display': 'none' });
                }
            }
        }
    }
}